import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import Image from 'components/AppImage';

const AccommodationComparison = ({ currentAccommodation = "Deluxe Forest Villa" }) => {
  const navigate = useNavigate();
  const [selectedForComparison, setSelectedForComparison] = useState([]);

  const accommodations = [
    {
      id: 1,
      name: "Deluxe Forest Villa",
      type: "Villa",
      image: "https://images.pexels.com/photos/271624/pexels-photo-271624.jpeg",
      price: 4500,
      maxGuests: 4,
      bedrooms: 2,
      bathrooms: 2,
      area: "850 sq ft",
      rating: 4.8,
      reviews: 124,
      features: [
        "Forest View",
        "Private Balcony",
        "Air Conditioning",
        "Free Wi-Fi",
        "Mini Refrigerator",
        "Coffee Maker",
        "Room Service",
        "Laundry Service"
      ],
      highlights: [
        "Panoramic forest views",
        "Spacious living area",
        "Premium bedding",
        "Welcome drinks included"
      ],
      popular: true
    },
    {
      id: 2,
      name: "River View Cottage",
      type: "Cottage",
      image: "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg",
      price: 3800,
      maxGuests: 3,
      bedrooms: 1,
      bathrooms: 1,
      area: "650 sq ft",
      rating: 4.6,
      reviews: 89,
      features: [
        "River View",
        "Private Garden",
        "Air Conditioning",
        "Free Wi-Fi",
        "Mini Refrigerator",
        "Coffee Maker",
        "Room Service"
      ],
      highlights: [
        "Direct river access",
        "Private garden space",
        "Peaceful location",
        "Bird watching spot"
      ],
      popular: false
    },
    {
      id: 3,
      name: "Luxury Tree House",
      type: "Tree House",
      image: "https://images.pexels.com/photos/1571468/pexels-photo-1571468.jpeg",
      price: 5200,
      maxGuests: 2,
      bedrooms: 1,
      bathrooms: 1,
      area: "500 sq ft",
      rating: 4.9,
      reviews: 67,
      features: [
        "Canopy View",
        "Unique Architecture",
        "Air Conditioning",
        "Free Wi-Fi",
        "Mini Refrigerator",
        "Coffee Maker",
        "Room Service",
        "Eco-Friendly"
      ],
      highlights: [
        "Unique tree house experience",
        "360-degree canopy views",
        "Eco-friendly design",
        "Adventure enthusiast favorite"
      ],
      popular: false
    },
    {
      id: 4,
      name: "Premium Safari Tent",
      type: "Tent",
      image: "https://images.pexels.com/photos/1571453/pexels-photo-1571453.jpeg",
      price: 3200,
      maxGuests: 2,
      bedrooms: 1,
      bathrooms: 1,
      area: "400 sq ft",
      rating: 4.4,
      reviews: 156,
      features: [
        "Safari Experience",
        "Outdoor Seating",
        "Fan Cooling",
        "Free Wi-Fi",
        "Mini Refrigerator",
        "Coffee Maker",
        "Shared Facilities"
      ],
      highlights: [
        "Authentic safari experience",
        "Close to wildlife",
        "Budget-friendly luxury",
        "Adventure activities nearby"
      ],
      popular: false
    }
  ];

  const currentRoom = accommodations?.find(room => room?.name === currentAccommodation);
  const otherRooms = accommodations?.filter(room => room?.name !== currentAccommodation);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const renderStars = (rating) => {
    return [...Array(5)]?.map((_, i) => (
      <Icon
        key={i}
        name="Star"
        size={14}
        className={i < Math.floor(rating) ? "text-accent fill-current" : "text-muted-foreground"}
      />
    ));
  };

  const handleCompareToggle = (roomId) => {
    setSelectedForComparison(prev => {
      if (prev?.includes(roomId)) {
        return prev?.filter(id => id !== roomId);
      } else if (prev?.length < 2) {
        return [...prev, roomId];
      }
      return prev;
    });
  };

  const handleViewDetails = (roomName) => {
    // In a real app, this would navigate to the specific accommodation details
    navigate('/accommodation-details', { state: { accommodation: roomName } });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="border-b border-border pb-6">
        <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
          Compare Accommodations
        </h2>
        <p className="text-muted-foreground">
          Explore other accommodation options and find the perfect fit for your stay
        </p>
      </div>
      {/* Current Accommodation Highlight */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
              Currently Viewing
            </div>
            {currentRoom?.popular && (
              <div className="bg-accent text-accent-foreground px-3 py-1 rounded-full text-sm font-medium">
                Most Popular
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="aspect-video rounded-lg overflow-hidden mb-4">
              <Image
                src={currentRoom?.image}
                alt={currentRoom?.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="lg:col-span-2">
            <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
              {currentRoom?.name}
            </h3>
            
            <div className="flex items-center space-x-4 mb-4 text-sm text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="Users" size={16} />
                <span>Up to {currentRoom?.maxGuests} guests</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Bed" size={16} />
                <span>{currentRoom?.bedrooms} bedroom{currentRoom?.bedrooms > 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Home" size={16} />
                <span>{currentRoom?.area}</span>
              </div>
            </div>

            <div className="flex items-center space-x-4 mb-4">
              <div className="flex items-center space-x-1">
                {renderStars(currentRoom?.rating)}
                <span className="text-sm text-muted-foreground ml-1">
                  ({currentRoom?.rating}) • {currentRoom?.reviews} reviews
                </span>
              </div>
              <div className="text-xl font-heading font-bold text-primary">
                {formatCurrency(currentRoom?.price)}/night
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {currentRoom?.highlights?.map((highlight, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">{highlight}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Other Accommodations */}
      <div>
        <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
          Other Available Options
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {otherRooms?.map((room) => (
            <div key={room?.id} className="bg-card border border-border rounded-lg overflow-hidden hover-lift">
              {/* Image */}
              <div className="aspect-video relative overflow-hidden">
                <Image
                  src={room?.image}
                  alt={room?.name}
                  className="w-full h-full object-cover"
                />
                {room?.popular && (
                  <div className="absolute top-3 left-3 bg-accent text-accent-foreground px-2 py-1 rounded-full text-xs font-medium">
                    Popular
                  </div>
                )}
                <div className="absolute top-3 right-3">
                  <button
                    onClick={() => handleCompareToggle(room?.id)}
                    className={`p-2 rounded-full transition-micro ${
                      selectedForComparison?.includes(room?.id)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-white/90 text-foreground hover:bg-white'
                    }`}
                  >
                    <Icon name="GitCompare" size={16} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-foreground">{room?.name}</h4>
                  <span className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground">
                    {room?.type}
                  </span>
                </div>

                <div className="flex items-center space-x-3 mb-3 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <Icon name="Users" size={14} />
                    <span>{room?.maxGuests}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Bed" size={14} />
                    <span>{room?.bedrooms}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Home" size={14} />
                    <span>{room?.area}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-2 mb-3">
                  <div className="flex items-center">
                    {renderStars(room?.rating)}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    ({room?.rating}) • {room?.reviews} reviews
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  {room?.highlights?.slice(0, 2)?.map((highlight, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{highlight}</span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-lg font-heading font-bold text-primary">
                    {formatCurrency(room?.price)}/night
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewDetails(room?.name)}
                  >
                    View Details
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Compare Button */}
      {selectedForComparison?.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <div className="bg-card border border-border rounded-lg shadow-organic-xl p-4">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-foreground">
                {selectedForComparison?.length} selected for comparison
              </span>
              <Button
                variant="default"
                size="sm"
                iconName="GitCompare"
                iconPosition="left"
                disabled={selectedForComparison?.length < 2}
              >
                Compare Now
              </Button>
              <button
                onClick={() => setSelectedForComparison([])}
                className="text-muted-foreground hover:text-foreground transition-micro"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccommodationComparison;